import React, { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Mic, Plus, Trash2, ExternalLink, X, RefreshCw, Save, Headphones, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

/**
 * AdminAudioDrops — Phase 24
 * CRUD for Coach Lewis's audio drops. v1 stores external URLs only (no file
 * upload) so we can ship without an object-storage integration.
 */

const CATEGORY_LABELS = {
  pregame: 'Game-Day Pump-Up',
  post_loss: 'Post-Loss Reset',
  mid_season: 'Mid-Season Grind',
  mindset: 'Mindset Drop',
};
const CATEGORY_OPTIONS = Object.keys(CATEGORY_LABELS);

const EMPTY = {
  id: null,
  title: '',
  description: '',
  audio_url: '',
  category: 'mindset',
  duration_sec: '',
  cover_image_url: '',
  published_at: '',
  order: 100,
  active: false,
};

function formatDuration(sec) {
  if (!sec || isNaN(sec)) return '—';
  const n = Math.max(0, Math.round(sec));
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function AdminAudioDrops() {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadDrops = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/audio-drops');
      setDrops(Array.isArray(data?.audio_drops) ? data.audio_drops : []);
    } catch (err) {
      logger.warn('admin audio drops load failed', err);
      toast.error(err.message || 'Could not load audio drops');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDrops(); }, []);

  const sorted = useMemo(() => {
    const list = [...drops];
    list.sort((a, b) => {
      const ap = a.published_at || a.created_at || '';
      const bp = b.published_at || b.created_at || '';
      return bp.localeCompare(ap);
    });
    return list;
  }, [drops]);

  const openNew = () => setEditor({ ...EMPTY });
  const openEdit = (d) => setEditor({
    id: d.id,
    title: d.title || '',
    description: d.description || '',
    audio_url: d.audio_url || '',
    category: d.category || 'mindset',
    duration_sec: d.duration_sec || '',
    cover_image_url: d.cover_image_url || '',
    // Convert ISO → datetime-local format YYYY-MM-DDTHH:MM
    published_at: d.published_at ? d.published_at.slice(0, 16) : '',
    order: d.order ?? 100,
    active: !!d.active,
  });
  const closeEditor = () => setEditor(null);
  const updateField = (k, v) => setEditor((e) => ({ ...e, [k]: v }));

  const handleSave = async () => {
    if (!editor) return;
    if (!editor.title.trim()) { toast.error('Title is required'); return; }
    if (!/^https?:\/\//i.test(editor.audio_url.trim())) {
      toast.error('Audio URL must start with http:// or https://');
      return;
    }
    setSaving(true);
    const payload = {
      title: editor.title.trim(),
      description: editor.description.trim() || null,
      audio_url: editor.audio_url.trim(),
      category: editor.category || 'mindset',
      duration_sec: editor.duration_sec ? Number(editor.duration_sec) : null,
      cover_image_url: editor.cover_image_url.trim() || null,
      published_at: editor.published_at
        ? new Date(editor.published_at).toISOString()
        : null,
      order: Number(editor.order) || 100,
      active: !!editor.active,
    };
    try {
      let result;
      if (editor.id) {
        result = await api.patch(`/api/admin/audio-drops/${editor.id}`, payload);
      } else {
        result = await api.post('/api/admin/audio-drops', payload);
      }
      const saved = result?.audio_drop;
      if (saved) {
        setDrops((prev) => {
          const i = prev.findIndex((x) => x.id === saved.id);
          if (i === -1) return [saved, ...prev];
          const next = [...prev];
          next[i] = saved;
          return next;
        });
      }
      toast.success(editor.id ? 'Audio drop updated' : 'Audio drop created');
      closeEditor();
    } catch (err) {
      toast.error(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d) => {
    try {
      const result = await api.patch(`/api/admin/audio-drops/${d.id}`, { active: !d.active });
      if (result?.audio_drop) {
        setDrops((prev) => prev.map((x) => (x.id === result.audio_drop.id ? result.audio_drop : x)));
      }
      toast.success(d.active ? 'Drop hidden' : 'Drop now live');
    } catch (err) {
      toast.error(err.message || 'Could not update');
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete "${d.title}" permanently?`)) return;
    try {
      await api.del(`/api/admin/audio-drops/${d.id}`);
      setDrops((prev) => prev.filter((x) => x.id !== d.id));
      toast.success('Audio drop deleted');
    } catch (err) {
      toast.error(err.message || 'Could not delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-audio-drops">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-foreground/60 font-semibold mb-1">
            The Locker Room
          </div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Audio drops</h2>
          <p className="text-[13.5px] text-foreground/60 mt-1">
            90-second locker-room talks gated to Locker Room Pass holders. Paste an MP3 or
            external share URL (Spotify episode, SoundCloud, etc.). Pass holders see an
            inline player on <a href="/locker-room" className="underline text-[#004C54]">/locker-room</a>;
            free users see a locked tile.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadDrops} disabled={loading} data-testid="admin-audio-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openNew} className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="admin-audio-new">
            <Plus className="h-4 w-4 mr-1.5" />
            Add audio drop
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-foreground/55">Loading…</div>
      ) : sorted.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-foreground/55">
            <Mic className="h-8 w-8 mx-auto mb-3 text-foreground/30" />
            No audio drops yet. Hit <strong>Add audio drop</strong> to publish the first one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((d) => (
            <Card key={d.id} className="hover:shadow-md transition-shadow" data-testid={`admin-audio-row-${d.id}`}>
              <CardContent className="p-4 sm:p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-[#004C54] flex items-center justify-center shrink-0">
                  <Mic className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    {d.active ? (
                      <Badge className="bg-[#004C54]/10 text-[#004C54] hover:bg-[#004C54]/10 border-0">Live</Badge>
                    ) : (
                      <Badge variant="outline" className="text-foreground/55">Hidden</Badge>
                    )}
                    <span className="font-mono-accent uppercase tracking-[0.18em] text-[9px] text-foreground/55 font-semibold">
                      {CATEGORY_LABELS[d.category] || d.category}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10.5px] text-foreground/40">
                      <Clock className="h-3 w-3" />
                      {formatDuration(d.duration_sec)}
                    </span>
                  </div>
                  <div className="font-heading text-[15px] font-semibold truncate">{d.title}</div>
                  {d.description && (
                    <div className="text-[12px] text-foreground/55 truncate mt-0.5">{d.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={d.audio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/55 hover:bg-secondary/60 hover:text-foreground transition-colors"
                    title="Open audio URL"
                    data-testid={`admin-audio-open-${d.id}`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleActive(d)}
                    data-testid={`admin-audio-toggle-${d.id}`}
                  >
                    {d.active ? 'Hide' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(d)}
                    data-testid={`admin-audio-edit-${d.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(d)}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid={`admin-audio-delete-${d.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor sheet */}
      {editor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="admin-audio-editor">
          <div className="w-full sm:max-w-2xl bg-background rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92vh] overflow-y-auto">
            <div className="p-5 sm:p-7">
              <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                  <div className="font-mono-accent uppercase tracking-[0.18em] text-[10px] text-foreground/55 font-semibold mb-1">
                    {editor.id ? 'Edit audio drop' : 'New audio drop'}
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold tracking-tight">
                    {editor.title || 'Untitled drop'}
                  </h3>
                </div>
                <Button variant="ghost" size="icon" onClick={closeEditor} disabled={saving}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="audio-title">Title *</Label>
                  <Input
                    id="audio-title"
                    value={editor.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    placeholder="Walk-out Lock-In"
                    maxLength={120}
                    data-testid="admin-audio-field-title"
                  />
                </div>

                <div>
                  <Label htmlFor="audio-desc">Description</Label>
                  <Textarea
                    id="audio-desc"
                    value={editor.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="A 90-second pre-game pump up. Pull this up on the bus."
                    rows={3}
                    maxLength={400}
                    data-testid="admin-audio-field-description"
                  />
                </div>

                <div>
                  <Label htmlFor="audio-url">Audio URL *</Label>
                  <Input
                    id="audio-url"
                    value={editor.audio_url}
                    onChange={(e) => updateField('audio_url', e.target.value)}
                    placeholder="https://… (MP3, SoundCloud, Spotify episode)"
                    data-testid="admin-audio-field-url"
                  />
                  <p className="text-[11.5px] text-foreground/50 mt-1">
                    Direct MP3 URLs play inline. SoundCloud / Spotify / other links open externally.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="audio-category">Category</Label>
                    <select
                      id="audio-category"
                      value={editor.category}
                      onChange={(e) => updateField('category', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="admin-audio-field-category"
                    >
                      {CATEGORY_OPTIONS.map((c) => (
                        <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="audio-duration">Duration (seconds)</Label>
                    <Input
                      id="audio-duration"
                      type="number"
                      min="0"
                      value={editor.duration_sec}
                      onChange={(e) => updateField('duration_sec', e.target.value)}
                      placeholder="90"
                      data-testid="admin-audio-field-duration"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="audio-published">Published at</Label>
                    <Input
                      id="audio-published"
                      type="datetime-local"
                      value={editor.published_at}
                      onChange={(e) => updateField('published_at', e.target.value)}
                      data-testid="admin-audio-field-published"
                    />
                  </div>
                  <div>
                    <Label htmlFor="audio-order">Order</Label>
                    <Input
                      id="audio-order"
                      type="number"
                      value={editor.order}
                      onChange={(e) => updateField('order', e.target.value)}
                      placeholder="100"
                      data-testid="admin-audio-field-order"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="audio-cover">Cover image URL (optional)</Label>
                  <Input
                    id="audio-cover"
                    value={editor.cover_image_url}
                    onChange={(e) => updateField('cover_image_url', e.target.value)}
                    placeholder="https://…"
                    data-testid="admin-audio-field-cover"
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="audio-active"
                      checked={!!editor.active}
                      onCheckedChange={(checked) => updateField('active', checked)}
                      data-testid="admin-audio-field-active"
                    />
                    <Label htmlFor="audio-active" className="cursor-pointer">
                      {editor.active ? 'Live to Pass holders' : 'Hidden'}
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={closeEditor} disabled={saving} data-testid="admin-audio-cancel">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-[#004C54] hover:bg-[#003a40] text-white"
                      data-testid="admin-audio-save"
                    >
                      <Save className="h-4 w-4 mr-1.5" />
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
